import { useGameContext } from '@/state/GameContext';
import { RARITY_COLORS } from '@/state/settings';
import { CraftingGrid } from './CraftingGrid';
import { ResultSlot } from './ResultSlot';

export function CraftingStation() {
  const game = useGameContext();
  const shown = game.result.kind === 'idle' ? game.preview : game.result;
  const gridHasItems = game.grid.some((c) => c);
  const known = shown.kind === 'vanilla' || shown.kind === 'custom';
  const canCraft = gridHasItems && game.result.kind !== 'loading' && shown.kind === 'idle';
  const canReroll = shown.kind === 'custom' && !shown.cached && game.cooldownRemaining <= 0;
  const showRerollLock = shown.kind === 'custom' || game.preview.kind === 'custom';

  return (
    <div className="panel station">
      <div className="panel-head">Crafting Table</div>
      <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'auto' }}>
        <div className="muted" style={{ alignSelf: 'flex-start', marginBottom: 6 }}>
          Interpretation: <b>{game.settings.interpretation}</b>
          {game.preview.kind !== 'idle' ? ' &middot; live preview' : ''}
        </div>
        <CraftingGrid />
        <ResultSlot />
        <div className="station-controls">
          <button className="btn primary" onClick={() => game.craft()} disabled={!canCraft && !known}>
            {game.result.kind === 'loading' ? 'Crafting...' : 'Craft'}
          </button>
          <button className="btn" onClick={() => game.clearGrid()} disabled={!gridHasItems}>
            Clear Grid
          </button>
          {canReroll ? (
            <button className="btn" onClick={() => game.reroll()} title="Reroll the AI result">
              Reroll{game.cooldownRemaining > 0 ? ` (${Math.ceil(game.cooldownRemaining / 1000)}s)` : ''}
            </button>
          ) : null}
        </div>

        {showRerollLock ? (
          <div className="row" style={{ marginTop: 10, fontSize: 11 }}>
            <label>Reroll lock:</label>
            <button
              className={`btn ghost${game.rerollLockTexture ? ' primary' : ''}`}
              onClick={() => game.setRerollLock(undefined, game.rerollLockCategory)}
              disabled={shown.kind !== 'custom'}
            >
              Texture {game.rerollLockTexture ? '\u2611' : '\u2610'}
            </button>
            <button
              className={`btn ghost${game.rerollLockCategory ? ' primary' : ''}`}
              onClick={() => game.setRerollLock(game.rerollLockTexture, undefined)}
              disabled={shown.kind !== 'custom'}
            >
              Category {game.rerollLockCategory ? '\u2611' : '\u2610'}
            </button>
            <span className="muted">lock prevents reroll from changing the selected texture/category</span>
          </div>
        ) : null}

        <RecipeDetails />
      </div>
    </div>
  );
}

function RecipeDetails() {
  const game = useGameContext();
  const shown = game.result.kind === 'idle' ? game.preview : game.result;
  if (shown.kind === 'custom') {
    const it = shown.item;
    return (
      <div className="details" style={{ width: '100%', maxWidth: 480, marginTop: 12 }}>
        <div className="row"><span className="lbl">Category</span><span>{it.category}</span></div>
        <div className="row"><span className="lbl">Tier / Rarity</span><span>Tier {it.tier} &middot; <b style={{ color: RARITY_COLORS[it.rarity] || '#fff' }}>{it.rarity}</b></span></div>
        <div className="row"><span className="lbl">Description</span><span>{it.description}</span></div>
        <div className="row"><span className="lbl">Traits</span><span>{it.traits.join(', ')}</span></div>
        <div className="row"><span className="lbl">Flags</span><span>{[it.isUpgrade && 'upgrade', it.isMachine && 'machine', it.isStructure && 'structure'].filter(Boolean).join(', ') || 'none'}</span></div>
        <div className="row"><span className="lbl">Texture</span><span>{it.textureId} {it.textureReused ? <span className="badge reuse">reused</span> : null}</span></div>
        {it.connectionExplanation ? <div className="explain"><b>Connection:</b> {it.connectionExplanation}</div> : null}
        {it.textureExplanation ? <div className="explain"><b>Texture:</b> {it.textureExplanation}</div> : null}
        <div className="muted" style={{ marginTop: 6 }}>Discovered {new Date(it.discoveredAt).toLocaleString()} &middot; model: {it.model}</div>
      </div>
    );
  }
  if (shown.kind === 'vanilla') {
    const meta = game.metaFor(shown.itemId);
    return (
      <div className="details" style={{ width: '100%', maxWidth: 420, marginTop: 12 }}>
        <div className="row"><span className="lbl">Result</span><span><b>{shown.name}</b> &times;{shown.count}</span></div>
        {meta ? <div className="row"><span className="lbl">Category</span><span>{meta.category}</span></div> : null}
        {meta?.tags?.length ? <div className="row"><span className="lbl">Tags</span><span>{meta.tags.join(', ')}</span></div> : null}
        <div className="explain"><b>Vanilla recipe.</b> This is a real Minecraft crafting recipe; the AI is not consulted.</div>
      </div>
    );
  }
  if (shown.kind === 'invalid') {
    return (
      <div className="details" style={{ width: '100%', maxWidth: 460, marginTop: 12 }}>
        <div className="explain"><b>Suggestion:</b> {shown.suggestion}</div>
      </div>
    );
  }
  return null;
}
